/**
 * Sidebar panel for re-editing a generator-backed target.
 *
 * Targets come in two flavours (see `GeneratorTarget`):
 *  - `{ kind: "object" }` — a single-mesh generator whose ref lives on a
 *    SceneObject. Rendered in the normal inspector sections list.
 *  - `{ kind: "collection" }` — a compound generator whose ref lives on
 *    a Collection. Rendered as the dedicated inspector body when a
 *    generator-backed collection is selected.
 *
 * Rendering is schema-driven: every entry in `generator.paramSpecs` is
 * translated to one row by `GeneratorParamRow`. Numbers become sliders,
 * booleans become eye toggles, enums become selects, and lists become
 * collapsible sub-sections that own their own `ListControl` + nested
 * param editor for the active item. Nesting is recursive — a list item's
 * sub-schema can itself contain any param type, including further lists.
 *
 * Param updates route through `adjustGeneratorParams`, which updates the
 * authoritative params immediately and schedules a latest-only background
 * rebuild. Undo batching is handled by a single `pushUndo` on the first
 * frame of a gesture (slider `onStart` / list item add/remove), so one
 * drag becomes one undo entry.
 */

import { Plus } from "lucide-react";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../../app/store.ts";
import type { GeneratorTarget } from "../../app/storeGeneratorOps.ts";
import { useOperatorContext } from "../../app/useOperatorContext.ts";
import { getGenerator } from "../../modeling/generators/registry.ts";
import type {
  GeneratorHideablePart,
  GeneratorListItem,
  GeneratorListItemType,
  GeneratorListParamSpec,
  GeneratorObjectRefParamSpec,
  GeneratorParamCondition,
  GeneratorParamSpec,
  GeneratorParams,
  GeneratorParamValue,
  GeneratorSectionParamSpec,
} from "../../modeling/generators/types.ts";
import { createListItemId } from "../../modeling/generators/types.ts";
import { getOperator, type Operator } from "../../modeling/operators.ts";
import type { ObjectKind } from "../../modeling/types.ts";
import { Button } from "../Button.tsx";
import { useDeferredUndo } from "../hooks/useDeferredUndo.ts";
import { SidebarCard } from "../SidebarCard.tsx";
import {
  CompactRow,
  EyeToggle,
  FoldoutSection,
  ListControl,
  SectionLabel,
  SegmentedControl,
  SelectSearch,
  SliderRow,
  Toggle,
} from "../shared.tsx";
import {
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_SM,
} from "../tokens.ts";

// ---------------------------------------------------------------------------
// Value formatters
// ---------------------------------------------------------------------------

function formatNumberValue(spec: GeneratorParamSpec, value: number): string {
  if (spec.type !== "number") return String(value);
  if (spec.step && spec.step >= 1) return String(Math.round(value));
  return value.toFixed(2);
}

/**
 * Display label for a list item: currently just its subtype label. The
 * identifying summary (wall, position) lives in the trailing slot so the
 * label column stays stable across edits.
 */
function listItemLabel(itemType: GeneratorListItemType): string {
  return itemType.label;
}

function listItemTrailing(
  item: GeneratorListItem,
  itemType: GeneratorListItemType,
): string | undefined {
  // Look for a wall/target enum + position pair — the Room case.
  const wall = item.params.wall;
  const pos = item.params.position;
  const parts: string[] = [];
  if (typeof wall === "string") {
    const wallSpec = itemType.paramSpecs.find((s) => s.id === "wall");
    if (wallSpec && wallSpec.type === "enum") {
      const opt = wallSpec.options.find((o) => o.value === wall);
      if (opt) parts.push(opt.label);
    } else {
      parts.push(wall);
    }
  }
  if (typeof pos === "number") parts.push(`${Math.round(pos * 100)}%`);
  return parts.length ? parts.join(" • ") : undefined;
}

const STRING_PARAM_PREVIEW_DEBOUNCE_MS = 350;

// ---------------------------------------------------------------------------
// Generator panel
// ---------------------------------------------------------------------------

export function GeneratorPanel({ target }: { target: GeneratorTarget }) {
  const adjustGeneratorParams = useStore((s) => s.adjustGeneratorParams);
  const commitGeneratorEdit = useStore((s) => s.commitGeneratorEdit);
  const applyGenerator = useStore((s) => s.applyGenerator);
  const setGeneratorAdjusting = useStore((s) => s.setGeneratorAdjusting);
  const { pushOnce, reset } = useDeferredUndo();
  const gestureActiveRef = useRef(false);
  const lingerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve the live ref from the store via a primitive/stable-ref
  // selector. Critical: the selector must not allocate — returning a
  // fresh `{...}` on every call would churn Zustand's equality check
  // and re-render indefinitely (React #185).
  const generatorId = useStore((s) =>
    target.kind === "object"
      ? (s.sceneObjects.get(target.id)?.generator?.generatorId ?? null)
      : (s.collections.get(target.id)?.generatorRef?.generatorId ?? null),
  );
  const params = useStore((s) =>
    target.kind === "object"
      ? (s.sceneObjects.get(target.id)?.generator?.params ?? null)
      : (s.collections.get(target.id)?.generatorRef?.params ?? null),
  );
  const memberCount = useStore((s) => {
    if (target.kind === "object") return 1;
    return s.collections.get(target.id)?.objectIds.size ?? 0;
  });

  // Stable handlers — hooks must run in the same order on every render,
  // so these come before the early `return null` below.
  const beginGesture = useCallback(() => {
    if (!gestureActiveRef.current) {
      pushOnce();
      gestureActiveRef.current = true;
    }
    if (lingerTimerRef.current !== null) {
      clearTimeout(lingerTimerRef.current);
      lingerTimerRef.current = null;
    }
    setGeneratorAdjusting(true);
  }, [pushOnce, setGeneratorAdjusting]);

  const endGesture = useCallback(() => {
    if (gestureActiveRef.current) {
      gestureActiveRef.current = false;
      reset();
      commitGeneratorEdit(target);
    }
    if (lingerTimerRef.current !== null) clearTimeout(lingerTimerRef.current);
    lingerTimerRef.current = setTimeout(() => {
      setGeneratorAdjusting(false);
      lingerTimerRef.current = null;
    }, 800);
  }, [commitGeneratorEdit, reset, target, setGeneratorAdjusting]);

  // Clear the linger timer and the flag only when this panel unmounts.
  // Depending on the whole deferred-undo object here caused the cleanup to
  // re-run on every render, which dropped `generatorAdjusting` mid-slider drag
  // and hid the transient wireframe overlay before the gesture finished.
  useEffect(
    () => () => {
      if (lingerTimerRef.current !== null) clearTimeout(lingerTimerRef.current);
      gestureActiveRef.current = false;
      reset();
      setGeneratorAdjusting(false);
    },
    [reset, setGeneratorAdjusting],
  );

  if (!generatorId || !params) return null;
  const gen = getGenerator(generatorId);
  if (!gen) return null;

  const isGroup = target.kind === "collection";

  const setParam = (id: string, value: GeneratorParamValue) => {
    const state = useStore.getState();
    const liveParams =
      target.kind === "object"
        ? state.sceneObjects.get(target.id)?.generator?.params
        : state.collections.get(target.id)?.generatorRef?.params;
    if (!liveParams) return;
    adjustGeneratorParams(target, { ...liveParams, [id]: value });
  };

  return (
    // pointerup resets the gesture flag so the *next* interaction starts a
    // fresh undo entry. We deliberately don't fire beginGesture on
    // pointerdown — a click that selects a list item without mutating it
    // shouldn't push an undo snapshot. Every control that actually mutates
    // state (slider onStart, toggle, enum, add/remove) calls beginGesture
    // explicitly before its change.
    <SidebarCard
      style={{ overflow: "hidden" }}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
    >
      <SectionLabel>{gen.label}</SectionLabel>
      {isGroup && (
        <div
          style={{
            fontSize: FONT_SIZE_TINY,
            color: COLOR_TEXT_FAINT,
            marginTop: -4,
            marginBottom: 4,
          }}
        >
          {memberCount} object{memberCount === 1 ? "" : "s"}
        </div>
      )}

      <ParamList
        key={`${target.kind}:${target.id}:${generatorId}`}
        specs={gen.paramSpecs}
        params={params}
        onChange={(id, v) => setParam(id, v)}
        onBeginGesture={beginGesture}
        onEndGesture={endGesture}
      />

      {isGroup && gen.hideableParts && gen.hideableParts.length > 0 && (
        <GeneratorPartVisibility
          collectionId={target.id}
          hideableParts={gen.hideableParts}
        />
      )}

      {gen.operatorIds && gen.operatorIds.length > 0 && (
        <GeneratorOperatorShortcuts
          target={target}
          operatorIds={gen.operatorIds}
        />
      )}

      <div
        style={{
          marginTop: 8,
          paddingTop: 6,
          borderTop: `0.5px solid ${COLOR_BORDER_STRONG}`,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="button"
          onClick={() => applyGenerator(target)}
          variant="secondary"
          size="xs"
        >
          Apply Generator
        </Button>
      </div>
    </SidebarCard>
  );
}

type LeafParamSpec = Exclude<GeneratorParamSpec, GeneratorSectionParamSpec>;

function defaultForSpec(spec: LeafParamSpec): GeneratorParamValue {
  return spec.type === "list" ? [...spec.default] : spec.default;
}

/**
 * Returns true when a param should be rendered given the current sibling
 * values. A param without a `showIf` condition is always visible.
 */
function specIsVisible(
  spec: GeneratorParamSpec,
  context: GeneratorParams,
): boolean {
  if (spec.hidden) return false;
  if (!spec.showIf) return true;
  const conditions = Array.isArray(spec.showIf) ? spec.showIf : [spec.showIf];
  return conditions.every((condition) => conditionMatches(condition, context));
}

function conditionMatches(
  condition: GeneratorParamCondition,
  context: GeneratorParams,
): boolean {
  const current = context[condition.id];
  const expected = condition.value;
  const matches = Array.isArray(expected)
    ? expected.includes(current as boolean | number | string)
    : current === expected;
  return condition.negate ? !matches : matches;
}

// ---------------------------------------------------------------------------
// ParamList — renders a list of GeneratorParamSpecs, dispatching sections to
// FoldoutSection wrappers and leaf specs to GeneratorParamRow. Used at both
// the top-level generator scope and inside list-item sub-editors so section
// support is automatic at every nesting depth.
// ---------------------------------------------------------------------------

function ParamList({
  specs,
  params,
  onChange,
  onBeginGesture,
  onEndGesture,
}: {
  specs: readonly GeneratorParamSpec[];
  /** The params record that provides values and showIf context for this level. */
  params: GeneratorParams;
  onChange: (id: string, value: GeneratorParamValue) => void;
  onBeginGesture: () => void;
  onEndGesture: () => void;
}) {
  return (
    <>
      {specs
        .filter((spec) => specIsVisible(spec, params))
        .map((spec) => {
          if (spec.type === "section") {
            return (
              <FoldoutSection
                key={spec.id}
                id={`gen-section:${spec.id}`}
                label={spec.label}
                defaultCollapsed={spec.defaultCollapsed}
              >
                <ParamList
                  specs={spec.params}
                  params={params}
                  onChange={onChange}
                  onBeginGesture={onBeginGesture}
                  onEndGesture={onEndGesture}
                />
              </FoldoutSection>
            );
          }
          return (
            <GeneratorParamRow
              key={spec.id}
              spec={spec}
              value={params[spec.id] ?? defaultForSpec(spec)}
              context={params}
              onChange={(v) => onChange(spec.id, v)}
              onBeginGesture={onBeginGesture}
              onEndGesture={onEndGesture}
            />
          );
        })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Individual param row — recursive: list items contain further rows
// ---------------------------------------------------------------------------

function GeneratorParamRow({
  spec,
  value,
  context,
  onChange,
  onBeginGesture,
  onEndGesture,
}: {
  spec: LeafParamSpec;
  value: GeneratorParamValue;
  context: GeneratorParams;
  onChange: (value: GeneratorParamValue) => void;
  onBeginGesture: () => void;
  onEndGesture: () => void;
}) {
  if (spec.type === "boolean") {
    return (
      <Toggle
        label={spec.label}
        checked={typeof value === "boolean" ? value : spec.default}
        onChange={(v) => {
          // Boolean toggles are single-shot — wrap the flip in its own
          // snapshot gesture since there's no drag to close.
          onBeginGesture();
          onChange(v);
          onEndGesture();
        }}
      />
    );
  }

  if (spec.type === "number") {
    const numValue = typeof value === "number" ? value : spec.default;
    const dynamicRange = spec.resolveRange?.(context) ?? {};
    const min = dynamicRange.min ?? spec.min ?? 0;
    const max = dynamicRange.max ?? spec.max ?? 1;
    const step = dynamicRange.step ?? spec.step ?? 1;
    const defaultValue = dynamicRange.default ?? spec.default;
    const sliderValue = Math.min(max, Math.max(min, numValue));
    return (
      <SliderRow
        density="compact"
        label={spec.label}
        value={sliderValue}
        min={min}
        max={max}
        step={step}
        displayValue={formatNumberValue(spec, sliderValue)}
        // Double-click the thumb to reset to the spec's default — the
        // Slider calls `onStart` first so our begin/end gesture hooks
        // still capture one undo entry per reset.
        defaultValue={defaultValue}
        onStart={onBeginGesture}
        onEnd={onEndGesture}
        onChange={(v) => onChange(v)}
        dimChromeOnDrag
      />
    );
  }

  if (spec.type === "enum") {
    const strValue = typeof value === "string" ? value : spec.default;
    if (spec.display === "segmented") {
      return (
        <CompactRow label={spec.label}>
          <SegmentedControl
            options={spec.options.map((o) => ({
              label: o.label,
              value: o.value,
            }))}
            value={strValue}
            onChange={(v) => {
              onBeginGesture();
              onChange(v);
              onEndGesture();
            }}
          />
        </CompactRow>
      );
    }
    return (
      <CompactRow label={spec.label}>
        <select
          value={strValue}
          onChange={(e) => {
            onBeginGesture();
            onChange(e.target.value);
            onEndGesture();
          }}
          style={ENUM_SELECT_STYLE}
        >
          {spec.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </CompactRow>
    );
  }

  if (spec.type === "string") {
    const strValue = typeof value === "string" ? value : spec.default;
    return (
      <GeneratorStringParamRow
        spec={spec}
        value={strValue}
        onChange={(v) => onChange(v)}
        onBeginGesture={onBeginGesture}
        onEndGesture={onEndGesture}
      />
    );
  }

  if (spec.type === "objectRef") {
    const refValue = typeof value === "string" ? value : null;
    return (
      <GeneratorObjectRefRow
        spec={spec}
        value={refValue}
        onChange={(v) => {
          // Object-ref picks are single-shot like enums / toggles — wrap
          // each change in its own undo snapshot since there's no drag
          // gesture to bracket. `onBeginGesture` / `onEndGesture` are the
          // same hooks that every other mutating control uses, so nested
          // list items keep reusing the parent's gesture state correctly.
          onBeginGesture();
          onChange(v);
          onEndGesture();
        }}
      />
    );
  }

  // spec.type === "list"
  return (
    <GeneratorListRow
      spec={spec}
      items={Array.isArray(value) ? value : spec.default}
      onChange={(next) => onChange(next)}
      onBeginGesture={onBeginGesture}
      onEndGesture={onEndGesture}
    />
  );
}

function GeneratorStringParamRow({
  spec,
  value,
  onChange,
  onBeginGesture,
  onEndGesture,
}: {
  spec: Extract<LeafParamSpec, { type: "string" }>;
  value: string;
  onChange: (value: string) => void;
  onBeginGesture: () => void;
  onEndGesture: () => void;
}) {
  const [draftValue, setDraftValue] = useState(value);
  const draftValueRef = useRef(value);
  const committedValueRef = useRef(value);
  const dirtyRef = useRef(false);
  const editingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const clearTimer = useCallback(() => {
    if (timerRef.current === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const commitValue = useCallback(
    (next: string) => {
      clearTimer();
      if (next === committedValueRef.current) {
        dirtyRef.current = false;
        return;
      }
      committedValueRef.current = next;
      dirtyRef.current = false;
      onChangeRef.current(next);
    },
    [clearTimer],
  );

  useEffect(() => {
    committedValueRef.current = value;
    if (!editingRef.current || !dirtyRef.current) {
      draftValueRef.current = value;
      setDraftValue(value);
    }
  }, [value]);

  useEffect(
    () => () => {
      const pendingValue = draftValueRef.current;
      clearTimer();
      if (dirtyRef.current && pendingValue !== committedValueRef.current) {
        committedValueRef.current = pendingValue;
        dirtyRef.current = false;
        onChangeRef.current(pendingValue);
      }
    },
    [clearTimer],
  );

  const scheduleCommit = useCallback(
    (next: string) => {
      clearTimer();
      timerRef.current = setTimeout(
        () => commitValue(next),
        STRING_PARAM_PREVIEW_DEBOUNCE_MS,
      );
    },
    [clearTimer, commitValue],
  );

  const updateDraft = (next: string) => {
    draftValueRef.current = next;
    setDraftValue(next);
    if (next === committedValueRef.current) {
      dirtyRef.current = false;
      clearTimer();
      return;
    }
    dirtyRef.current = true;
    scheduleCommit(next);
  };

  const handleFocus = () => {
    editingRef.current = true;
    onBeginGesture();
  };

  const handleBlur = () => {
    editingRef.current = false;
    commitValue(draftValueRef.current);
    onEndGesture();
  };

  return (
    <CompactRow label={spec.label}>
      {spec.display === "multiline" ? (
        <textarea
          value={draftValue}
          placeholder={spec.placeholder}
          maxLength={spec.maxLength}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={(e) => updateDraft(e.target.value)}
          rows={3}
          style={STRING_TEXTAREA_STYLE}
        />
      ) : (
        <input
          type="text"
          value={draftValue}
          placeholder={spec.placeholder}
          maxLength={spec.maxLength}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={(e) => updateDraft(e.target.value)}
          style={STRING_INPUT_STYLE}
        />
      )}
    </CompactRow>
  );
}

const ENUM_SELECT_STYLE: CSSProperties = {
  flex: 1,
  fontSize: FONT_SIZE_SMALL,
  color: COLOR_TEXT_DIM,
  background: "transparent",
  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
  borderRadius: RADIUS_SM,
  padding: "2px 4px",
  outline: "none",
  fontFamily: "inherit",
  minWidth: 0,
};

const STRING_INPUT_STYLE: CSSProperties = {
  flex: 1,
  fontSize: FONT_SIZE_SMALL,
  color: COLOR_TEXT_DIM,
  background: COLOR_SURFACE,
  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
  borderRadius: RADIUS_SM,
  padding: "3px 6px",
  outline: "none",
  fontFamily: "inherit",
  minWidth: 0,
};

const STRING_TEXTAREA_STYLE: CSSProperties = {
  ...STRING_INPUT_STYLE,
  resize: "vertical",
  minHeight: 48,
  lineHeight: 1.35,
};

// ---------------------------------------------------------------------------
// objectRef param — picker for referencing another SceneObject by id
// ---------------------------------------------------------------------------

/**
 * SelectSearch-backed picker for an `objectRef` param. Lists every scene
 * object whose `objectKind` is allowed by `spec.objectKinds` (or all
 * objects when that filter is absent). Storing / exchanging values as
 * SceneObject ids — not names — so refs keep working after renames.
 *
 * The eyedropper forwards the store's `beginEyedropperSession` directly:
 * picked object ids are the same currency the picker consumes, so no
 * translation layer is needed.
 */
function GeneratorObjectRefRow({
  spec,
  value,
  onChange,
}: {
  spec: GeneratorObjectRefParamSpec;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const sceneObjects = useStore((s) => s.sceneObjects);
  const options = useMemo(() => {
    const kindFilter = spec.objectKinds as readonly ObjectKind[] | undefined;
    return Array.from(sceneObjects.values())
      .filter((obj) => !kindFilter || kindFilter.includes(obj.objectKind))
      .map((obj) => ({ value: obj.id, label: obj.name }));
  }, [sceneObjects, spec.objectKinds]);

  return (
    <CompactRow label={spec.label}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <SelectSearch
          value={value}
          options={options}
          onChange={(v) => onChange(v)}
          placeholder="None"
          eyedropperPick={(onPick, onCancel) => {
            const kindFilter = spec.objectKinds as
              | readonly ObjectKind[]
              | undefined;
            useStore.getState().beginEyedropperSession(
              (id) => onPick(id),
              onCancel,
              (id) => {
                const obj = useStore.getState().sceneObjects.get(id);
                return (
                  !!obj && (!kindFilter || kindFilter.includes(obj.objectKind))
                );
              },
            );
          }}
        />
      </div>
    </CompactRow>
  );
}

// ---------------------------------------------------------------------------
// List param row — ListControl + nested editor for the active item
// ---------------------------------------------------------------------------

function GeneratorListRow({
  spec,
  items,
  onChange,
  onBeginGesture,
  onEndGesture,
}: {
  spec: GeneratorListParamSpec;
  items: readonly GeneratorListItem[];
  onChange: (next: readonly GeneratorListItem[]) => void;
  onBeginGesture: () => void;
  onEndGesture: () => void;
}) {
  // Active selection is local UI state — never stored in the generator.
  const [activeId, setActiveId] = useState<string | null>(null);

  // Clamp activeId if the active item was removed or the list became empty.
  const activeItem = useMemo(() => {
    if (activeId == null) return items[0] ?? null;
    return items.find((i) => i.id === activeId) ?? items[0] ?? null;
  }, [activeId, items]);

  const activeItemType = useMemo(() => {
    if (!activeItem) return null;
    return spec.itemTypes.find((t) => t.id === activeItem.itemType) ?? null;
  }, [activeItem, spec.itemTypes]);

  // Map list items to ListControl rows with subtype label + summary trailing.
  const listRows = useMemo(
    () =>
      items.map((item) => {
        const itemType = spec.itemTypes.find((t) => t.id === item.itemType);
        return {
          id: item.id,
          label: itemType ? listItemLabel(itemType) : item.itemType,
          trailing: itemType ? (
            <span
              style={{
                fontSize: FONT_SIZE_TINY,
                color: COLOR_TEXT_FAINT,
                marginRight: 4,
              }}
            >
              {listItemTrailing(item, itemType) ?? ""}
            </span>
          ) : undefined,
        };
      }),
    [items, spec.itemTypes],
  );

  const addItem = (itemTypeId: string) => {
    const itemType = spec.itemTypes.find((t) => t.id === itemTypeId);
    if (!itemType) return;
    const newItem: GeneratorListItem = {
      id: createListItemId(),
      itemType: itemType.id,
      params: { ...itemType.defaultParams },
    };
    onBeginGesture();
    onChange([...items, newItem]);
    onEndGesture();
    setActiveId(newItem.id);
  };

  const removeItem = (id: string) => {
    onBeginGesture();
    onChange(items.filter((i) => i.id !== id));
    onEndGesture();
  };

  const updateItemParam = (
    itemId: string,
    paramId: string,
    value: GeneratorParamValue,
  ) => {
    onChange(
      items.map((i) =>
        i.id === itemId
          ? { ...i, params: { ...i.params, [paramId]: value } }
          : i,
      ),
    );
  };

  // Footer add controls — one button per item subtype when there are
  // multiple, or fall back to the built-in `+` button when there's only one.
  const singleType = spec.itemTypes.length === 1;

  const footerActions = !singleType
    ? spec.itemTypes.map((it) => (
        <Button
          key={it.id}
          type="button"
          onClick={() => addItem(it.id)}
          title={`Add ${it.label}`}
          variant="ghost"
          size="xs"
          leftIcon={<Plus size={10} />}
          style={{ height: 20, minHeight: 20, padding: "0 6px" }}
        >
          {it.label}
        </Button>
      ))
    : undefined;

  return (
    <div style={{ marginTop: 8 }}>
      <SectionLabel>{spec.label}</SectionLabel>
      <ListControl
        items={listRows}
        activeId={activeItem?.id ?? null}
        onSelect={setActiveId}
        onAdd={singleType ? () => addItem(spec.itemTypes[0].id) : undefined}
        onDelete={removeItem}
        footerActions={footerActions}
        emptyMessage={`No ${spec.label.toLowerCase()}`}
        maxHeight={140}
      />
      {activeItem && activeItemType && (
        <div
          style={{
            marginTop: 6,
            padding: "4px 0 2px",
            borderTop: `0.5px solid ${COLOR_BORDER_STRONG}`,
          }}
        >
          <ParamList
            specs={activeItemType.paramSpecs}
            params={activeItem.params}
            onChange={(paramId, v) =>
              updateItemParam(activeItem.id, paramId, v)
            }
            onBeginGesture={onBeginGesture}
            onEndGesture={onEndGesture}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Operator shortcuts — buttons that invoke operators from the `OPERATORS`
// registry on the generator's owner. Declared by `Generator.operatorIds`.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// GeneratorPartVisibility — per-child show/hide toggles for compound outputs
// ---------------------------------------------------------------------------

/**
 * Render an eye toggle per `hideableParts` entry the generator declares.
 * Each toggle drives the global `visibility` map on the child SceneObject
 * resolved via the collection's `roleToObjectId`, so show/hide is
 * non-destructive — materials, transforms, and selection stay intact
 * across toggles, and the toggle mirrors the state users see in the
 * hierarchy panel's own eye icons.
 *
 * A part's `dependentRoles` (e.g. molding, baseboard, doors/windows for a
 * wall) are resolved against the live `roleToObjectId` and flipped to
 * the same state in one batch so the whole logical unit hides or shows
 * together under a single undo entry.
 *
 * Entries whose primary role isn't present in the live `roleToObjectId`
 * (e.g. a part gated behind another param) are filtered out rather than
 * shown disabled: a missing role means the child genuinely doesn't exist
 * right now, so a "hide" affordance would be nonsensical. Dependent
 * roles are looked up the same way — any that are absent are skipped
 * silently, which lets generators emit the full possible set without
 * having to replicate gating logic.
 */
function GeneratorPartVisibility({
  collectionId,
  hideableParts,
}: {
  collectionId: string;
  hideableParts: readonly GeneratorHideablePart[];
}) {
  const roleToObjectId = useStore(
    (s) => s.collections.get(collectionId)?.generatorRef?.roleToObjectId,
  );
  const params = useStore(
    (s) => s.collections.get(collectionId)?.generatorRef?.params,
  );
  const visibility = useStore((s) => s.visibility);
  const setObjectsVisibility = useStore((s) => s.setObjectsVisibility);

  if (!roleToObjectId || !params) return null;

  const rows = hideableParts
    .map((part) => {
      const objectId = roleToObjectId[part.roleId];
      if (!objectId) return null;
      const ids: string[] = [objectId];
      if (part.dependentRoles) {
        for (const role of part.dependentRoles(params)) {
          const id = roleToObjectId[role];
          if (id) ids.push(id);
        }
      }
      return { part, ids };
    })
    .filter(
      (r): r is { part: GeneratorHideablePart; ids: string[] } => r !== null,
    );

  if (rows.length === 0) return null;

  return (
    <FoldoutSection id="gen-section:visibility" label="Visibility">
      {rows.map(({ part, ids }) => {
        // The primary id anchors the toggle's displayed state — dependents
        // always follow along, so their prior state (if anyone had
        // diverged them via the hierarchy panel) is irrelevant here.
        const visible = visibility.get(ids[0]) ?? true;
        return (
          <EyeToggle
            key={part.roleId}
            label={part.label}
            checked={visible}
            onChange={(next) => setObjectsVisibility(ids, next)}
          />
        );
      })}
    </FoldoutSection>
  );
}

/**
 * Render one button per operator id a generator surfaces. Clicking a
 * button scopes the selection to the generator's owner so the operator's
 * store-state lookup picks the right target, then calls `execute()`.
 *
 * Enablement is read from the operator's own `isEnabled(ctx)` predicate
 * against a freshly built `OperatorContext`, so a button greys out when
 * the operator wouldn't be usable from the operator panel either — we
 * never desync the two surfaces.
 */
function GeneratorOperatorShortcuts({
  target,
  operatorIds,
}: {
  target: GeneratorTarget;
  operatorIds: readonly string[];
}) {
  const selectObjectTarget = useStore((s) => s.selectObjectTarget);
  const ctx = useOperatorContext();

  const operators = useMemo(() => {
    const out: Operator[] = [];
    for (const id of operatorIds) {
      const op = getOperator(id);
      if (op) out.push(op);
    }
    return out;
  }, [operatorIds]);

  if (operators.length === 0) return null;

  const runOperator = (op: Operator) => {
    selectObjectTarget(
      target.kind === "object"
        ? { kind: "sceneObject", id: target.id }
        : { kind: "collection", id: target.id },
      false,
    );
    op.execute();
  };

  return (
    <div
      style={{
        marginTop: 8,
        paddingTop: 6,
        borderTop: `0.5px solid ${COLOR_BORDER_STRONG}`,
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
      }}
    >
      {operators.map((op) => {
        const enabled = op.isEnabled(ctx);
        const Icon = op.icon;
        return (
          <Button
            key={op.id}
            type="button"
            onClick={enabled ? () => runOperator(op) : undefined}
            disabled={!enabled}
            title={op.label}
            variant="secondary"
            size="xs"
            leftIcon={<Icon size={12} />}
          >
            {op.label}
          </Button>
        );
      })}
    </div>
  );
}
